import { useMsal } from "@azure/msal-react";
import { login, verifyAdUser } from "api/auth";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { EyeIconOpenPassword, EyeIconPassword } from "themes/Icon";
import ButtonComp from "themes/elements/Button";
import InputComp from "themes/elements/Input";
import {
  storePermissions,
  storeToken,
  storeUserData,
} from "utils/cryptoStorage";

import AddUserImage from "../../../../public/svg/windows.svg";
import { loginRequest } from "../../../authConfig";
import Overlay from "../Overlay";
import style from "../auth.module.scss";

function SignInForm() {
  const { instance } = useMsal();
  const navigate = useNavigate();
  const newPasswordRef = useRef(null);
  const [formData, setFormData] = useState({ username: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [adLoading, setAdLoading] = useState(false);
  const [verifyAdLoading, setVerifyAdLoading] = useState(false); // Track verifyAdUser call
  const [error, setError] = useState("");
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    localStorage.clear();
  }, []);

  const handleChange = (key, value) => {
    setFormData((prevFormData) => ({ ...prevFormData, [key]: value }));
  };

  const handleLogin = async (event) => {
    event.preventDefault();
    setLoading(true);

    if (!formData.username) {
      toast.warning("Please enter an email address.");
      setLoading(false);
      return;
    }

    if (!emailRegex.test(formData.username)) {
      toast.warning("Please enter a valid email address.");
      setLoading(false);
      return;
    }

    if (!formData.password) {
      toast.warning("Please enter a password.");
      setLoading(false);
      return;
    }

    try {
      const data = await login(formData.username, formData.password);

      if (data?.accessToken) {
        storeToken("accessToken", data.accessToken);
        storeToken("refreshToken", data.refreshToken);

        if (data?.expires_in) {
          const expiryTime = new Date(data.expires_in * 1000).toISOString();
          storeToken("tokenExpiryTime", expiryTime);
        }

        if (data.data) {
          storeUserData(JSON.stringify(data.data));
          if (data.data.permissions) {
            storePermissions(data.data.permissions);
            toast.success("Login Successfully");
            navigate("/");
          } else {
            throw new Error("No permissions available.");
          }
        } else {
          throw new Error("No user data available.");
        }
      }
    } catch (error) {
      setError("Login failed.");
    } finally {
      setLoading(false);
    }
  };

  const handleTogglePassword = () => {
    const input = newPasswordRef.current?.input;
    if (input) {
      const pos = input.selectionStart ?? input.value.length;
      setShowPassword((prev) => !prev);
      setTimeout(() => {
        input.focus();
        input.setSelectionRange(pos, pos);
      }, 0);
    }
  };

  const handleADLogin = async (loginType) => {
    try {
      const response =
        loginType === "popup"
          ? await instance.loginPopup(loginRequest)
          : await instance.loginRedirect(loginRequest);

      if (response) {
        const { accessToken, refreshToken } = response;
        await handleSuccessfulLogin(response);
        await performAdditionalApiCall(accessToken); // Ensure only one API call is made
        if (refreshToken) {
          localStorage.setItem("refreshToken", refreshToken);
        }
      }
    } catch (error) {}
  };

  const performAdditionalApiCall = async (accessToken) => {
    if (verifyAdLoading) {
      return; // Prevent API call if it's already in progress
    }

    try {
      setVerifyAdLoading(true); // Start loading
      const response = await verifyAdUser({ action: "sign-in" }, accessToken);
      if (response?.data) {
        await handleSuccessfulLogin(response.data);
        toast.success("Login Successfully");
      }
    } catch (error) {
    } finally {
      setVerifyAdLoading(false); // End loading
    }
  };

  // Processes the successful login
  const handleSuccessfulLogin = (data) => {
    const { access_token, expires_in } = data;

    storeToken("accessToken", access_token);

    if (expires_in) {
      const expiryTime = new Date(expires_in * 1000).toISOString();
      storeToken("tokenExpiryTime", expiryTime);
    }

    if (data) {
      storeUserData(JSON.stringify(data));
      if (data.permissions) storePermissions(data.permissions);
      navigate("/");
    }
  };

  useEffect(() => {
    if (instance) {
      const handleResponse = async () => {
        try {
          const response = await instance.handleRedirectPromise();
          setAdLoading(true);
          if (response) {
            const { accessToken } = response;
            await performAdditionalApiCall(accessToken); // Ensure only one API call
          }
        } catch (error) {
        } finally {
          setAdLoading(false);
        }
      };

      handleResponse();
    }
  }, [instance]);

  return (
    <div
      className={`flex flex-column ${style.formContainer}`}
      style={{ padding: "20px" }}
    >
      {error && <p className={style.error}>{error}</p>}
      <div className="flex flex-column gap-5">
        <InputComp
          label="Username*"
          labelClass={style["input-label"]}
          placeholder="Enter your username"
          id="username"
          value={formData.username}
          onChange={(e) => handleChange("username", e.target.value)}
        />
        <div className={style.passwordInputContainer}>
          <InputComp
            ref={newPasswordRef}
            label="Password*"
            labelClass={style["input-label"]}
            placeholder="Enter your password"
            id="password"
            type={showPassword ? "text" : "password"} // Toggle between text and password
            value={formData.password}
            onChange={(e) => handleChange("password", e.target.value)}
            icon={
              <span
                style={{
                  opacity: formData.password ? 1 : 0,
                  transition: "opacity 0.3s",
                }}
              >
                {!showPassword ? (
                  <EyeIconPassword onClick={handleTogglePassword} /> // Show the closed eye icon
                ) : (
                  <EyeIconOpenPassword onClick={handleTogglePassword} /> // Show the open eye icon
                )}
              </span>
            }
            onIconClick={handleTogglePassword}
          />
        </div>
      </div>
      <button
        onClick={() => navigate("/forgot")}
        className={style["forgot-btn"]}
      >
        Forgot password?
      </button>
      <ButtonComp wFull height={48} className="mt-8" onClick={handleLogin}>
        {loading ? "Logging in..." : "Continue"}
      </ButtonComp>
      <p className={style.or_content}>Or log in with</p>
      <div className={style.adv_image_container}>
        <ButtonComp
          variant="secondary"
          wFull
          height={48}
          className=" flex"
          onClick={() => handleADLogin("redirect")}
          disabled={loading || verifyAdLoading} // Disable if API is in progress
        >
          <span className={style.adv_image}>
            <img src={AddUserImage} alt="microsoft" />
          </span>
          Microsoft Active Directory
        </ButtonComp>
      </div>
      {adLoading && <Overlay />}
    </div>
  );
}

export default SignInForm;
